/// <reference types="vite/client" />

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

if (!import.meta.env.VITE_TENANT_ID || !import.meta.env.VITE_CLIENT_ID) {
  throw new Error(
    'Missing required environment variables for authentication configuration'
  );
}

const TENANT_ID = import.meta.env.VITE_TENANT_ID;
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID;
const AUTHORITY_DOMAIN = 'login.microsoftonline.com';

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: TENANT_ID,
  },
  authorities: {
    signUpSignIn: {
      authority: `https://${AUTHORITY_DOMAIN}/${TENANT_ID}`,
    },
  },
  authorityDomain: AUTHORITY_DOMAIN,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: window.location.origin, // Use the current origin as redirect URI
    postLogoutRedirectUri: window.location.origin, // Redirect to origin after logout
    navigateToLoginRequestUrl: true, // Navigate back to the original request location after login
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    secureCookies: false, // Set this to "true" if your environment is using secure cookies
  },
  system: {
    allowNativeBrowker: false, // Set this to "true" if you want to use the MSAL.js wrapper library with a native MSAL implementation
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii) return;

        // Add a filter for excessive ID token logs
        if (message.includes('CacheManager:getIdToken')) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      LogLevel: LogLevel.Info,
      piiLoggingEnabled: false,
    },
  },
};

export const isAuthBypassed = import.meta.env.VITE_BYPASS_AUTH === 'true';
export const isAuthEnabled = !isAuthBypassed;
